import type { ActionFunctionArgs } from "react-router-dom";
import { redirect } from "react-router-dom";
import { organizationAPI, APIError } from "../../lib/api";
import {
  createOrganizationSchema,
  updateOrganizationSchema,
  inviteMemberSchema,
} from "../../schemas";
import { generateSlug, extractFieldErrors, parseFormData } from "../../utils";
import type { ActionResult } from "./auth.actions";

export async function createOrganizationAction({
  request,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const formData = await parseFormData(request);

  if (!formData.slug && formData.name) {
    formData.slug = generateSlug(formData.name);
  }

  const result = createOrganizationSchema.safeParse(formData);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    const organization = await organizationAPI.create({
      name: result.data.name,
      slug: result.data.slug,
    });
    return redirect(`/organizations/${organization.id}`);
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function updateOrganizationAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const { id } = params;

  if (!id) {
    return { success: false, error: "Organization ID is required" };
  }

  const formData = await parseFormData(request);
  const result = updateOrganizationSchema.safeParse(formData);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    const organization = await organizationAPI.update(id, result.data);
    return { success: true, data: organization };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function deleteOrganizationAction({
  params,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const { id } = params;

  if (!id) {
    return { success: false, error: "Organization ID is required" };
  }

  try {
    await organizationAPI.delete(id);
    return redirect("/organizations");
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function inviteMemberAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const { id } = params;

  if (!id) {
    return { success: false, error: "Organization ID is required" };
  }

  const formData = await parseFormData(request);
  const result = inviteMemberSchema.safeParse(formData);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    await organizationAPI.addMember(id, result.data);
    return redirect(`/organizations/${id}`);
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function updateMemberRoleAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult> {
  const { id, userId } = params;

  if (!id || !userId) {
    return { success: false, error: "Organization ID and User ID are required" };
  }

  const formData = await parseFormData(request);
  const role = formData.role;

  if (!role || !["owner", "admin", "member"].includes(role)) {
    return { success: false, error: "Valid role is required" };
  }

  try {
    const member = await organizationAPI.updateMemberRole(id, userId, {
      role: role as "owner" | "admin" | "member",
    });
    return { success: true, data: member };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function removeMemberAction({ params }: ActionFunctionArgs): Promise<ActionResult> {
  const { id, userId } = params;

  if (!id || !userId) {
    return { success: false, error: "Organization ID and User ID are required" };
  }

  try {
    await organizationAPI.removeMember(id, userId);
    return { success: true };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}
